import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { withBreakpoints } from "@utils/breakpoints/breakpoints.provider";
import { SearchComponent } from "@utils/search/@types/searchComponent";

import {
    getSearchStatus,
    hasResults,
} from "@redux/modules/value/search/search.selectors";
import { SearchStatus } from "@redux/modules/value/search/search.types";

import { SearchResultsComponent } from "@components/Search/SearchResults/searchResults.component";

import { InitSearch } from "./components/InitSearch/initSearch.container";
import { SaveScrollPosition } from "./components/SaveScrollPosition/saveScrollPosition.container";
import { SyncRoute } from "./components/SyncRoute/syncRoute.container";
import { SearchResultsSkeleton } from "./searchResults.skeleton";

const SEARCH_NO_RESULTS_ID = "searchResults__noResults";

export type SearchResultProductTileConfig = {
    showButton: boolean;
    allowInstantAddToCart: boolean;
    showCategoryLink: boolean;
    useInsidePadding: boolean;
    amountOfFeatures: number;
    lazyLoad: boolean;
};

type SearchResultsProps = {
    searchFilters?: string;
    query?: string;
    productTileConfig?: SearchResultProductTileConfig;
    showFacets?: boolean;
    categoryPagePaths?: Array<string>;
};

const _SearchResults = ({
    searchFilters,
    query,
    productTileConfig,
    showFacets = true,
    categoryPagePaths,
}: SearchResultsProps) => {
    const [renderNoResults, setRenderNoResults] = useState(false);

    const status = useSelector(getSearchStatus);

    const searchStatus = useSelector(getSearchStatus);
    const hasProductResults = useSelector(
        hasResults(SearchComponent.PRODUCT_NUMBER_OF_HITS),
    );
    const hasContentResults = useSelector(
        hasResults(SearchComponent.CONTENT_NUMBER_OF_HITS),
    );

    const results = useMemo(
        () => hasProductResults || hasContentResults,
        [hasProductResults, hasContentResults],
    );

    useEffect(() => {
        const noResultsComponent =
            document.getElementById(SEARCH_NO_RESULTS_ID);

        if (!results && status !== SearchStatus.Loading) {
            noResultsComponent?.classList.remove(
                "searchResults__noResults--hidden",
            );
            setRenderNoResults(true);
        } else {
            noResultsComponent?.classList.add(
                "searchResults__noResults--hidden",
            );
            setRenderNoResults(false);
        }
    }, [searchStatus, results, setRenderNoResults]);

    return (
        <>
            {status === SearchStatus.Loading && !results && (
                <SearchResultsSkeleton />
            )}
            {(status !== SearchStatus.Loading || results) && (
                <SearchResultsComponent
                    productTileConfig={productTileConfig}
                    showFacets={showFacets}
                />
            )}
            <SaveScrollPosition />
            <InitSearch
                searchFilters={searchFilters}
                query={query}
                categoryPagePaths={categoryPagePaths}
            />
            <SyncRoute ignoreQuery={!!query} />
        </>
    );
};

export const SearchResults = withBreakpoints(_SearchResults);
